import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";

import { findCountryByValue } from '../../lib/countries/countries.utils'

import Actions from "./ColumnHelpers/Actions";
import Menus from "./ColumnHelpers/Menus";
import MenuItems from "./ColumnHelpers/MenuItems";
import MenuSections from "./ColumnHelpers/MenuSections";
import QrCodes from "./ColumnHelpers/QrCodes";

import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";

import { FileImagePlaceholder } from '../../icons'

import { Language } from '../../types/menuTypes';
import { Menu } from '../../types/menuTypes';
import { ImgType } from "../../types/imageTypes";
import { Restaurant } from "../../types/restaurantTypes";

export type DataProps = Menu & {
  id: number;
  website?: string;
  phone?: string;
  name?: string;
  img?: ImgType;
  description?: string;
  countryCode?: string;
  languages?: Language[];
  mainLanguage?: string;
}

const columnHelper = createColumnHelper<DataProps>();

type Props = {
  data?: DataProps[] | Restaurant[] | undefined;
  columns?: string[] | undefined;
  initialPageSize?: number;
  rowKey: string;
};

const DynamicTable = ({ data = [], initialPageSize = 5, columns: columnsProp, rowKey }: Props) => {
  const allColumns = [
    columnHelper.display({
      id: "img",
      header: "Image",
      cell: (info) => {
        const original = info.row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 overflow-hidden rounded-full">
              {original?.img?.small
                ? <img width={40} height={40} src={original?.img?.small} alt={original?.name} />
                : <FileImagePlaceholder />
              }
              
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: () => "Name",
      cell: (info) => info.getValue<string | undefined>(),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => "Phone",
      cell: (info) => info.getValue<string | undefined>(),
    }),
    columnHelper.accessor("website", {
      id: "website",
      header: () => "Web site",
      cell: (info) => info.getValue<string | undefined>(),
    }),
    columnHelper.display({
      id: "qrCode",
      header: "QR Code",
      cell: (info) => QrCodes(info.row.original),
    }),
    columnHelper.display({
      id: "menus",
      header: "Menus",
      cell: (info) => Menus(info.row.original.id),
    }),
    columnHelper.display({
      id: "menuSections",
      header: "Sections",
      cell: (info) => MenuSections(info.row.original.id),
    }),
    columnHelper.display({
      id: "menuItems",
      header: "Items",
      cell: (info) => {
        const withoutMenuSections = info.row.original?.menuSections?.length === 0;
        return MenuItems(info.row.original.id, withoutMenuSections);
      },
    }),
    columnHelper.display({
      id: "countryCode",
      header: "Language",
      cell: (info) => {
        const { countryCode } = info.row.original;
        const country = findCountryByValue(countryCode)
        return (
          <span className="truncate flex items-center text-gray-900 dark:text-white/90">
              <img
                alt={`${countryCode}`}
                src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode?.toUpperCase()}.svg`}
                className={"inline mr-2 h-4 rounded-sm"}
              />
              {country?.title}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "languages",
      header: "Languages",
      cell: (info) => {
        const { languages, mainLanguage } = info.row.original;
        return (
          <ul className="flex flex-wrap gap-1">
            {languages?.map((l: Language) => {
              const country = findCountryByValue(l.language)
              return (
                <li key={l.position}
                  className={`${
                    mainLanguage === l.language? "bg-success-500 text-white dark:text-white" : "bg-gray-100 dark:bg-white/5 text-white dark:text-white/80"
                  } p-1 inline-flex items-center px-2.5 justify-center gap-1 rounded-full font-medium text-sm`}
                >    
                  {country?.flag}
                </li>
              )
            })}
          </ul>
        )
      },
    }),
    columnHelper.display({
      id: "editAction",
      header: "Edit",
      cell: (info) => {
        return (
          <div className="flex gap-4">
            {Actions(['edit', 'delete'], rowKey, info.row.original.id)}
          </div>
        )
      }  
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => "Description",
      cell: (info) => info.getValue<string | undefined>()
    }),
  ];

  const [sorting, setSorting] = React.useState<SortingState>([]);
  
  const visibleColumns = React.useMemo(() => {
    if (!columnsProp || columnsProp.length === 0) return allColumns;
    return allColumns.filter((col) => {
      const id: string | undefined = col.id;
      return id ? columnsProp.includes(id) : false;
    });
  }, [columnsProp]);

  const table = useReactTable({
    data: (data as DataProps[]),
    columns: visibleColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: initialPageSize } },
  });

  const isColumnVisible = (id: string) => visibleColumns.some(col => col.id === id);

  return (
    <div className="w-full space-y-4">
      {/* Mobile View (Cards) */}
      <div className="block md:hidden space-y-4">
        {table.getRowModel().rows.map((row) => {
          const item = row.original;
          const withoutMenuSections = item.menuSections?.length === 0;
          return (
            <div
              key={row.id}
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/[0.05] dark:bg-white/[0.03] space-y-3"
            >
              <div className="flex items-start gap-3">
                {isColumnVisible("img") && item.img?.small && (
                  <div className="w-12 h-12 overflow-hidden rounded-full flex-shrink-0 self-center">
                    <img src={item.img.small} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  {isColumnVisible("name") && (
                    <h3 className="font-semibold text-gray-900 dark:text-white text-base truncate">
                      {item.name}
                    </h3>
                  )}
                  
                  {isColumnVisible("phone") && item.phone && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      📞 {item.phone}
                    </p>
                  )}

                  {isColumnVisible("website") && item.website && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      🌐 <a href={item.website} target="_blank" rel="noreferrer" className="hover:underline">{item.website}</a>
                    </p>
                  )}
                </div>
              </div>

              {isColumnVisible("countryCode") && item.countryCode && (
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <img
                    alt={item.countryCode}
                    src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${item.countryCode.toUpperCase()}.svg`}
                    className="h-3.5 rounded-sm"
                  />
                  <span>{findCountryByValue(item.countryCode)?.title}</span>
                </div>
              )}

              {isColumnVisible("languages") && item.languages && item.languages.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {item.languages.map((l) => {
                    const country = findCountryByValue(l.language);
                    return (
                      <span
                        key={l.position}
                        title={country?.title}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.mainLanguage === l.language
                            ? "bg-success-500 text-white"
                            : "bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-gray-300"
                        }`}
                      >
                        {country?.flag || l.language}
                      </span>
                    );
                  })}
                </div>
              )}

              {isColumnVisible("description") && item.description && (
                <p className="min-h-min text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-white/[0.01] p-2 rounded-lg">
                  {item.description}
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-white/[0.05]">
                {isColumnVisible("qrCode") && QrCodes(item)}
                {isColumnVisible("menus") && Menus(item.id)}
                {isColumnVisible("menuSections") && MenuSections(item.id)}
                {isColumnVisible("menuItems") && MenuItems(item.id, withoutMenuSections)}
              </div>

              {isColumnVisible("editAction") && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-white/[0.05]">
                  {Actions(['edit', 'delete'], rowKey, item.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="max-w-full overflow-x-auto">
          <div className="w-full">
            <Table>
              <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        isHeader
                        className="px-4 py-4 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className="flex items-center gap-2 cursor-pointer select-none"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {header.column.columnDef.header instanceof Function
                              ? header.column.columnDef.header(header.getContext())
                              : header.column.columnDef.header}
                            {{
                              asc: " 🔼",
                              desc: " 🔽",
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className="px-4 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>    
          </div>
        </div>
      </div>

      {table.getPageCount() > 1 && (
        <div className="flex items-center justify-between gap-2 bg-white dark:bg-white/[0.03] px-4 py-3 rounded-xl border border-gray-200 dark:border-white/[0.05]">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              className="px-2.5 py-1 text-sm bg-gray-100 dark:bg-white/5 rounded disabled:opacity-50"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              {"<<"}
            </button>
            <button
              className="px-2.5 py-1 text-sm bg-gray-100 dark:bg-white/5 rounded disabled:opacity-50"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {"<"}
            </button>
            <button
              className="px-2.5 py-1 text-sm bg-gray-100 dark:bg-white/5 rounded disabled:opacity-50"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {">"}
            </button>
            <button
              className="px-2.5 py-1 text-sm bg-gray-100 dark:bg-white/5 rounded disabled:opacity-50"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              {">>"}
            </button>
          </div>
        </div>
      )}
    </div>  
  );
}

export default DynamicTable;
